import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { Avatar, AvatarFallback } from "../../../components/ui/avatar";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { adminNavLinks } from "./navLinks";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const isHrefActive = (href, pathname) => {
  if (!href) return false;
  const [basePath, query] = href.split("?");
  if (query) {
    return (
      pathname + window.location.search === href ||
      window.location.href.includes(href.replace("/app", ""))
    );
  }
  if (basePath === "/app") return pathname === "/app";
  return pathname === basePath || pathname.startsWith(basePath + "/");
};

const hasActiveDescendant = (item, pathname) => {
  if (!item.children) return false;
  return item.children.some((child) => {
    if (child.href && isHrefActive(child.href, pathname)) return true;
    if (child.children) return hasActiveDescendant(child, pathname);
    return false;
  });
};

// Reusable permission check for a nav item
const canSeeItem = (item, hasPermission, hasAllPermissions) => {
  if (item.permissionAll || item.permissionAny) {
    const allOk =
      !item.permissionAll ||
      item.permissionAll.length === 0 ||
      hasAllPermissions(item.permissionAll);
    const anyOk =
      !item.permissionAny ||
      item.permissionAny.length === 0 ||
      hasPermission(item.permissionAny);
    return allOk && anyOk;
  }
  if (item.permission && item.permission.length > 0) {
    return hasPermission(item.permission);
  }
  return true; // no permission → visible
};

// Filter nav tree by permission, recursively (works for 2 or 3 levels)
const filterNavLinks = (links, hasPermission, hasAllPermissions) => {
  return links
    .map((item) => {
      if (item.children) {
        const visibleChildren = filterNavLinks(
          item.children,
          hasPermission,
          hasAllPermissions,
        );
        if (visibleChildren.length === 0) return null;
        return { ...item, children: visibleChildren };
      }
      return canSeeItem(item, hasPermission, hasAllPermissions) ? item : null;
    })
    .filter(Boolean);
};

const NestedDropdown = ({ item, pathname, collapsed, onItemClick }) => {
  const [open, setOpen] = useState(() => hasActiveDescendant(item, pathname));
  const isActive = hasActiveDescendant(item, pathname) || open;

  if (collapsed) {
    const firstHref = item.children?.[0]?.href ?? "#";
    return (
      <Link
        to={firstHref}
        onClick={onItemClick}
        className={cn(
          "flex items-center justify-center rounded-md px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-[#3a4a6b] text-white/90"
            : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
        )}
        title={item.label}
      >
        <FontAwesomeIcon icon={item.icon} className="h-4 w-4 shrink-0" />
      </Link>
    );
  }

  return (
    <li>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-[#3a4a6b] text-white/90"
            : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
        )}
      >
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={item.icon} className="h-4 w-4 shrink-0" />
          <span>{item.label}</span>
        </div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <ul className="ml-4 mt-1 space-y-1 border-l border-[#394356] pl-2">
          {item.children.map((grandchild) => {
            const active = grandchild.href
              ? window.location.search.includes(
                  grandchild.href.split("?")[1] ?? "",
                ) && pathname.startsWith(grandchild.href.split("?")[0])
              : false;
            return (
              <li key={grandchild.href}>
                <Link
                  to={grandchild.href}
                  onClick={onItemClick}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-[#455070] text-white"
                      : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
                  )}
                >
                  <FontAwesomeIcon
                    icon={grandchild.icon}
                    className="h-4 w-4 shrink-0"
                  />
                  <span>{grandchild.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

// ─── AdminSideBar
const AdminSideBar = ({ collapsed, onToggle, onItemClick, mobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    logout,
    user,
    userAccount,
    hasPermission,
    hasAllPermissions,
  } = useAuth();
  const pathname = location.pathname;
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const visibleLinks = filterNavLinks(
    adminNavLinks,
    hasPermission,
    hasAllPermissions,
  );

  const toggleDropdown = (label) => {
    if (collapsed && !mobile) return;
    setOpenDropdowns((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isDropdownActive = (item) => {
    if (!item.children) return false;
    return item.children.some((child) => {
      if (child.href) {
        const [basePath] = child.href.split("?");
        return pathname.startsWith(basePath);
      }
      if (child.children) {
        return child.children.some((grandchild) => {
          if (!grandchild.href) return false;
          const [basePath] = grandchild.href.split("?");
          return pathname.startsWith(basePath);
        });
      }
      return false;
    });
  };

  const isChildActive = (href) => {
    if (!href) return false;
    const [basePath, queryString] = href.split("?");
    if (queryString) {
      return (
        pathname.startsWith(basePath) && location.search.includes(queryString)
      );
    }
    return pathname === href || pathname.startsWith(href + "/");
  };

  // Handle logout
  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setIsLoggingOut(true);
      await logout();
      toastSuccess("Logged out successfully");
      navigate("/login");
    } catch (error) {
      toastError("Failed to logout. Please try again.");
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getUserInitials = () => {
    if (!user) return "U";

    const name = userAccount?.full_name || user?.full_name || "User";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserName = () => {
    if (!user) return "User";
    return userAccount?.full_name || user?.full_name || "User";
  };

  // Get user role display
  const getUserRole = () => {
    if (!user) return "";
    const roleMap = {
      super_admin: "Super Admin",
      admin: "Administrator",
      owner: "Shop Owner",
      seller: "Shop Seller",
    };
    return roleMap[user.role] || user.role;
  };

  return (
    <aside
      className={cn(
        "sticky top-0 flex bg-[#242e42] h-screen flex-col text-white/80 border-r transition-all duration-300 border-[#394356] overflow-hidden shrink-0",
        collapsed && !mobile ? "w-[68px]" : "w-[250px]",
      )}
    >
      {/* User profile */}
      <div className="flex flex-col items-center gap-2 px-3 py-6 border-b border-[#394356] shrink-0">
        <Avatar
          className={cn(
            "transition-all",
            collapsed && !mobile ? "h-10 w-10" : "h-20 w-20",
          )}
        >
          <AvatarFallback className="bg-[#364159] text-white">
            {getUserInitials()}
          </AvatarFallback>
        </Avatar>
        {(!collapsed || mobile) && (
          <div className="text-center">
            <p className="text-sm font-semibold text-white">{getUserName()}</p>
            <p className="text-xs text-[#d3d9de] opacity-70">{getUserRole()}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="flex flex-col gap-1">
          {visibleLinks.map((item) => {
            if (item.children) {
              const isOpen = openDropdowns[item.label];
              const hasActiveChild = isDropdownActive(item);

              return (
                <li key={item.label}>
                  {collapsed && !mobile ? (
                    <Link
                      to={
                        item.children.find((c) => c.href)?.href ??
                        item.children[0]?.children?.[0]?.href ??
                        "#"
                      }
                      onClick={onItemClick}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors justify-center",
                        hasActiveChild
                          ? "bg-[#364159] text-white/90"
                          : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
                      )}
                      title={item.label}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="h-5 w-5 shrink-0"
                      />
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleDropdown(item.label)}
                        className={cn(
                          "w-full flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors",
                          hasActiveChild || isOpen
                            ? "bg-[#364159] text-white/90"
                            : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={item.icon}
                            className="h-5 w-5 shrink-0"
                          />
                          <span>{item.label}</span>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={cn(
                            "h-3 w-3 transition-transform",
                            isOpen && "rotate-180",
                          )}
                        />
                      </button>

                      {isOpen && (
                        <ul className="ml-4 mt-1 space-y-1 border-l border-[#394356] pl-2">
                          {item.children.map((child) => {
                            if (child.children) {
                              return (
                                <NestedDropdown
                                  key={child.label}
                                  item={child}
                                  pathname={pathname}
                                  collapsed={collapsed}
                                  onItemClick={onItemClick}
                                />
                              );
                            }

                            return (
                              <li key={child.href}>
                                <Link
                                  to={child.href}
                                  onClick={onItemClick}
                                  className={cn(
                                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                                    isChildActive(child.href)
                                      ? "bg-[#3a4a6b] text-white/90"
                                      : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
                                  )}
                                >
                                  <FontAwesomeIcon
                                    icon={child.icon}
                                    className="h-4 w-4 shrink-0"
                                  />
                                  <span>{child.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}
                </li>
              );
            }

            const isActive =
              pathname === item.href ||
              (item.href !== "/app" && pathname.startsWith(item.href));

            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  onClick={onItemClick}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors",
                    isActive
                      ? "bg-[#364159] text-white/90"
                      : "text-[#d3d9de] hover:bg-[#364159] hover:text-white/90",
                    collapsed && !mobile && "justify-center px-2",
                  )}
                  title={collapsed && !mobile ? item.label : undefined}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="h-5 w-5 shrink-0"
                  />
                  {(!collapsed || mobile) && <span>{item.label}</span>}
                </Link>
              </li>
            );
          })}

          {/* Logout Button */}
          <li>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={cn(
                "w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "text-[#d3d9de] hover:bg-[#364159] hover:text-white/95",
                collapsed && !mobile && "justify-center px-2",
                isLoggingOut && "opacity-50 cursor-not-allowed",
              )}
            >
              <FontAwesomeIcon
                icon={faRightFromBracket}
                className="h-5 w-5 shrink-0"
              />
              {(!collapsed || mobile) && (
                <span>{isLoggingOut ? "Logging out..." : "Logout"}</span>
              )}
              {isLoggingOut && (
                <span className="ml-auto h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
            </button>
          </li>
        </ul>
      </nav>

      {/* Collapse toggle */}
      {!mobile && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border bg-white text-gray-700 shadow-md hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 flex items-center justify-center p-0"
        >
          {collapsed ? (
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
          ) : (
            <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
          )}
        </Button>
      )}
    </aside>
  );
};

export default AdminSideBar;